import azureAuthService from '../services/azureAuthService';
import { session, type AccessDecision } from '../services/auth/session';

interface ProtectedRouteDeps {
  /** Lets the sign-in library finish a return from Microsoft; resolves true when it signed in. */
  processRedirectReturn: () => Promise<boolean>;
  checkAccess: () => Promise<AccessDecision>;
  goToLogin: (search: string) => void;
}

export const createProtectedRouteLoader =
  (deps: ProtectedRouteDeps) => async (): Promise<null> => {
    // A return from Microsoft must be processed before the stored token is looked at.
    const urlParams = new URLSearchParams(window.location.search);
    const msalLoginInProgress = sessionStorage.getItem('msalLoginInProgress');
    if (
      (urlParams.has('code') && urlParams.has('state')) ||
      msalLoginInProgress
    ) {
      sessionStorage.removeItem('msalLoginInProgress');
      if (await deps.processRedirectReturn()) {
        sessionStorage.setItem('hasProcessedLogin', 'true');
        return null;
      }
    }

    // Just after a sign-in, the stored token may not be readable yet.
    if (sessionStorage.getItem('hasProcessedLogin')) {
      setTimeout(() => {
        sessionStorage.removeItem('hasProcessedLogin');
      }, 1000);
      return null;
    }

    // 'signInRedirect' means the session is already navigating to Microsoft.
    if ((await deps.checkAccess()) === 'login') {
      deps.goToLogin(window.location.search);
    }
    return null;
  };

export const protectedRouteLoader = createProtectedRouteLoader({
  processRedirectReturn: async () => !!(await azureAuthService.initialize()),
  checkAccess: () => session.checkAccess(),
  goToLogin: (search) => {
    window.location.href = `/login${search}`;
  },
});
